import { useState } from "react";
import { Clock } from "lucide-react";
import type { Convenio } from "@/api/contrato";
import { useAlterarConvenio, useBancos, useConvenio } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import {
  alteraOQueViaja,
  camposNaTela,
  comOQueViajaGravado,
  emissaoEmAndamento,
  janelaFechada,
  linhaDaConta,
  regrasDoBanco,
  useValoresDoConvenio,
  validarEdicao,
  valoresDe,
} from "./convenio";
import { EsqueletoDoFormulario, FormularioDeConvenio } from "./FormularioDeConvenio";
import { errosDoFormulario, textoDaRecusa } from "./recusas";

const AVISO_DA_FILA = "Há boleto a caminho do banco. Número, carteira, variação e byte aguardam a fila.";
const NADA_SALVO = "Nada foi salvo.";

/*
 * A edição do convênio, no formulário da criação, aberta por `?abrir=editar`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ AS DUAS RECUSAS DE ALTERAÇÃO NÃO SE PARECEM, E NÃO PODEM SE PARECER.     │
 * │                                                                          │
 * │ `alteracao_bancaria_fechada` é definitiva: há boleto emitido, e a saída  │
 * │ é no banco. O valor recusado sai do campo e o campo trava, ou a tela     │
 * │ ficaria mostrando, travado, um número que não foi gravado.               │
 * │                                                                          │
 * │ `emissao_em_andamento` passa sozinha quando a fila esvazia. O que foi    │
 * │ digitado fica, em faixa âmbar, e o botão vira Repetir.                   │
 * │                                                                          │
 * │ Nas duas, o `PATCH` não gravou nada, nem a descrição que veio junto: o   │
 * │ modal fica aberto, sem aviso de sucesso, e a recusa diz isso.            │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function EditarConvenio({
  convenio,
  aoFechar,
}: {
  convenio: Convenio;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const catalogo = useBancos(cliente, true);
  const alterar = useAlterarConvenio(cliente, convenio.id);
  const releitura = useConvenio(cliente, convenio.id);

  const regras = regrasDoBanco(bancoDoCatalogo(catalogo.data?.bancos ?? [], convenio.banco));
  const { valores, erros, setErros, mudar, reescrever } = useValoresDoConvenio(
    valoresDe(convenio),
    regras,
  );

  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [recusaDaFila, setRecusaDaFila] = useState<string | undefined>(undefined);
  const [recusaDaJanela, setRecusaDaJanela] = useState(false);

  const pronto = catalogo.data !== undefined;
  const erroDeLeitura =
    pronto || catalogo.error === null ? undefined : textoDaRecusa(catalogo.error);

  // Sinal ausente vale como `false` (spec): a tela não promete o que a API pode recusar.
  const janelaAberta = convenio.alteracao_bancaria_permitida === true && !recusaDaJanela;

  function recusar(falha: unknown) {
    setRecusaDaFila(undefined);

    if (janelaFechada(falha)) {
      setRecusaDaJanela(true);
      reescrever((atuais) => comOQueViajaGravado(atuais, convenio));
      setErros({});
      setErroGeral(`${textoDaRecusa(falha)} ${NADA_SALVO}`);
      void releitura.refetch();
      return;
    }

    if (emissaoEmAndamento(falha)) {
      setErroGeral(undefined);
      setRecusaDaFila(`${textoDaRecusa(falha)} ${NADA_SALVO}`);
      void releitura.refetch();
      return;
    }

    const { porCampo, geral } = errosDoFormulario(
      falha,
      camposNaTela(regras, valores.modo_registro, convenio),
    );
    setErros(porCampo);
    setErroGeral(geral);
  }

  function enviar() {
    setErroGeral(undefined);

    const validacao = validarEdicao(convenio, valores, regras, janelaAberta);
    if (!validacao.ok) {
      setErros(validacao.erros);
      return;
    }

    // `PATCH` sem chave nenhuma é `400 invalid_body`: sem mudança, não chama a rota.
    if (Object.keys(validacao.corpo).length === 0) {
      aoFechar();
      return;
    }

    alterar.mutate(validacao.corpo, {
      onSuccess: () => {
        avisos.feito("Convênio alterado");
        aoFechar();
      },
      onError: recusar,
    });
  }

  const avisoDaFila =
    recusaDaFila ??
    (convenio.emissao_a_caminho === true && janelaAberta && alteraOQueViaja(convenio, valores)
      ? AVISO_DA_FILA
      : undefined);

  function rotuloDeEnvio() {
    if (erroDeLeitura !== undefined) return "Tentar de novo";
    return recusaDaFila === undefined ? "Salvar" : "Repetir";
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Editar convênio"
      descricao={linhaDaConta(convenio)}
      aoEnviar={pronto ? enviar : () => void catalogo.refetch()}
      enviando={alterar.isPending || (!pronto && erroDeLeitura === undefined)}
      erro={erroDeLeitura ?? erroGeral}
      rotuloDeEnvio={rotuloDeEnvio()}
    >
      {pronto ? (
        <FormularioDeConvenio
          valores={valores}
          erros={erros}
          regras={regras}
          gravado={convenio}
          janelaFechada={!janelaAberta}
          aoMudar={mudar}
        />
      ) : (
        erroDeLeitura === undefined && <EsqueletoDoFormulario />
      )}

      {avisoDaFila !== undefined && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-controle border border-transito/40 bg-transito/10 px-3 py-2 text-corpo text-transito"
        >
          <Clock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {avisoDaFila}
        </p>
      )}
    </ModalDeCadastro>
  );
}
